import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import AddIcon from "@mui/icons-material/Add";
import Paper from "@mui/material/Paper";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CategoryIcon from "@mui/icons-material/Category";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HeightIcon from "@mui/icons-material/Height";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import BrushIcon from "@mui/icons-material/Brush";
import OpacityIcon from "@mui/icons-material/Opacity";
import DeleteIcon from "@mui/icons-material/Delete";
import GradientIcon from "@mui/icons-material/Gradient";
import ColorizeIcon from "@mui/icons-material/Colorize";
import Tooltip from "@mui/material/Tooltip";
import {
  SettingSlider,
  Setting,
  SettingDropSelector,
  SettingColorSelector,
} from "./SpreadsheetSettingSharedComponents";
import {
  selectorSheet,
  selectorSheetCondition,
  actionSheetConditionAdded,
  actionSheetConditionUpdated,
  actionSheetConditionRemoved,
  actionSheetFileUpdated,
  actionSheetHeatmapCreated,
  actionSheetHeatmapRemoved,
  actionSheetHeatmapColorUpdated,
} from ".";
import {
  defaultSheetFile,
  heatMapObjfromJson,
  parseNumber,
} from "./spreadsheetutils";
import SharedIconSelector from "./IconSelector";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const formControl_ = (theme) => ({
  margin: "0px",
  minWidth: 80,
});

const button_ = (theme) => ({
  margin: "10px",
});

const sizeIcon_ = (theme) => ({
  transform: "rotate(45deg)",
});

const condition_ = (theme) => ({
  minWidth: 275,
  marginBottom: "8px",
  "&:hover": {
    backgroundColor: "rgba(217, 217, 217, 0.2)",
  },
  width: "100%",
  padding: "10px",
});

const conditionHeader_ = (theme) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const radioChecked_ = (theme) => ({
  color: "var(--fill-color)",
});

// Inspired from: https://github.com/mui/material-ui/issues/37226
//
// Workaround limitations in Safari's native date picker.
// NOTE: Because of the @supports clause, this ONLY affects Safari.
//
// Without this, Safari would show today's date whenever the date
// value is null/undefined.
// With this, it'll show what most other browsers show.
//
// This is not perfect for several reasons:
// - if user has locale settings set to dd/mm/yyyy, this will still
//   blindly show mm/dd/yyyyy
// - this does not show the calendar icon like every other browser's
//   native date picker does
//
// At least this makes the experience a little more consistent across
// browsers.
const date_ = (theme) => ({
  "& .MuiInputBase-input": {
    "@supports (font: -apple-system-body) and (-webkit-appearance: none)": {
      '&[data-empty="true"]': {
        color: "rgba(0, 0, 0, 0)",
        position: "relative",
        minWidth: "120px",

        "&::before": {
          content: '"mm/dd/yyyy"',
          color: "#333",
          display: "inline-block",
          position: "absolute",
        },
      },
    },
  },
});

const SizeIcon = () => {
  return <HeightIcon sx={sizeIcon_} />;
};

const TextSelector = ({
  sheetId,
  active,
  labelIcon,
  column,
  handleColSelect,
}) => {
  const { json } = useSelector(selectorSheet(sheetId));

  const textColumnHeaders = json[0]
    .map((header, index) => {
      return {
        value: index,
        label: header,
      };
    })
    .filter((item) => item.label);

  const handleChange = (text) => {
    handleColSelect(text);
  };

  return (
    <SettingDropSelector
      active={active}
      label="Attribute"
      labelIcon={labelIcon}
      formControlClass={formControl_}
      value={column}
      handleSelection={handleChange}
      optionsArray={textColumnHeaders}
      defaultOption={{
        value: "",
        label: "Select Column",
        disabled: true,
      }}
    />
  );
};

const OpacitySlider = ({ sheetId, conditionId, ...rest }) => {
  const { fillOpacity: reduxFillOpacity } = useSelector(selectorSheet(sheetId));
  const { fillOpacity: defaultFillOpacity } = defaultSheetFile(sheetId);
  const { fillOpacity: conditionFillOpacity } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleNewOpacity = (value) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          fillOpacity: value,
          id: conditionId,
        },
      }),
    );
  };

  const value =
    typeof conditionFillOpacity === "number"
      ? conditionFillOpacity
      : typeof reduxFillOpacity === "number"
        ? reduxFillOpacity
        : typeof defaultFillOpacity === "number"
          ? defaultFillOpacity
          : 0.5;

  return (
    <SettingSlider
      value={value}
      min={0}
      max={1}
      step={0.1}
      valueLabelDisplay="off"
      handleNewValue={handleNewOpacity}
      label="Transparency"
      labelIcon={<OpacityIcon />}
      {...rest}
    />
  );
};

const TextSizeSlider = ({ sheetId, conditionId }) => {
  const { textSize: reduxTextSize } = useSelector(selectorSheet(sheetId));
  const { textSize: defaultTextSize } = defaultSheetFile(sheetId);
  const { textSize: conditionTextSize } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleNewTextSize = (value) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          textSize: value,
        },
      }),
    );
  };

  const value = conditionTextSize || reduxTextSize || defaultTextSize;

  return (
    <SettingSlider
      value={value}
      min={0}
      max={32}
      valueLabelDisplay="off"
      handleNewValue={handleNewTextSize}
      label="Size"
      labelIcon={<SizeIcon />}
    />
  );
};

const IconSizeSlider = ({ sheetId, conditionId }) => {
  const { iconSize: defaultIconSize } = defaultSheetFile(sheetId);
  const { iconSize: reduxIconSize } = useSelector(selectorSheet(sheetId));
  const { iconSize: conditionIconSize } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleNewIconSize = (value) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          iconSize: value,
        },
      }),
    );
  };

  const value =
    typeof conditionIconSize === "number"
      ? conditionIconSize
      : typeof reduxIconSize === "number"
        ? reduxIconSize
        : typeof defaultIconSize === "number"
          ? defaultIconSize
          : 10;

  return (
    <SettingSlider
      value={value}
      min={0}
      max={3}
      step={0.1}
      valueLabelDisplay="off"
      handleNewValue={handleNewIconSize}
      label="Size"
      labelIcon={<SizeIcon />}
    />
  );
};
const PointSizeSlider = ({ sheetId, conditionId, ...rest }) => {
  const { pointSize: defaultPointSize } = defaultSheetFile(sheetId);
  const { pointSize: reduxPointSize } = useSelector(selectorSheet(sheetId));
  const { pointSize: conditionPointSize } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleNewIconSize = (value) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          pointSize: value,
        },
      }),
    );
  };

  const value =
    typeof conditionPointSize === "number"
      ? conditionPointSize
      : typeof reduxPointSize === "number"
        ? reduxPointSize
        : typeof defaultPointSize === "number"
          ? defaultPointSize
          : 10;

  return (
    <SettingSlider
      value={value}
      min={0}
      max={3}
      step={0.1}
      valueLabelDisplay="off"
      handleNewValue={handleNewIconSize}
      label="Size"
      labelIcon={<SizeIcon />}
      {...rest}
    />
  );
};

const IconSelector = ({ sheetId, conditionId, ...rest }) => {
  const { icon: defaultIcon } = useSelector(selectorSheet(sheetId));
  const { icon: conditionIcon } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const icon = conditionIcon || defaultIcon || "";

  const handleNewIcon = (newIcon) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          icon: newIcon,
        },
      }),
    );
  };

  return (
    <SharedIconSelector
      value={icon}
      handleSelection={handleNewIcon}
      {...rest}
    />
  );
};

const PointIconSelector = ({ sheetId, conditionId, ...rest }) => {
  const { pointIcon: defaultPointIcon } = defaultSheetFile(sheetId);
  const { pointIcon: reduxPointIcon } = useSelector(selectorSheet(sheetId));
  const { pointIcon: conditionPointIcon } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const icon = conditionPointIcon || reduxPointIcon || defaultPointIcon;

  const handleNewIcon = (newIcon) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          pointIcon: newIcon,
        },
      }),
    );
  };

  return (
    <SharedIconSelector
      value={icon}
      handleSelection={handleNewIcon}
      {...rest}
    />
  );
};

const HeatMapSwitch = (props) => {
  const { sheetId, conditionId, catObj, ...rest } = props;
  const { type, hslObj } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const newHslObj = hslObj || { h: 0, s: 1, l: 0.5 };

  const handleHeatMapSwitch = (event) => {
    if (type === "color" || type === "point") {
      let heatMapObj = heatMapObjfromJson(catObj, newHslObj);
      dispatch(
        actionSheetHeatmapCreated({
          conditionId,
          sheetId,
          heatMapObj,
          catObj,
          hslObj: newHslObj,
        }),
      );
    } else {
      dispatch(actionSheetHeatmapRemoved({ conditionId, sheetId }));
    }
  };

  return (
    <Setting
      label="Heatmap"
      {...rest}
      labelIcon={<GradientIcon />}
      controller={
        <Switch
          checked={type.indexOf("heatmap") === 0}
          onChange={handleHeatMapSwitch}
          name="checkedA"
          slotProps={{
            input: {
              "aria-label": "secondary checkbox",
            },
          }}
        />
      }
    />
  );
};

const FillColorSelector = ({ sheetId, conditionId, ...rest }) => {
  const { fillColor: reduxFillColor } = useSelector(selectorSheet(sheetId));
  const { fillColor: defaultFillColor } = defaultSheetFile(sheetId);
  const {
    fillColor: conditionFillColor,
    type,
    catObj,
    hslObj,
  } = useSelector(selectorSheetCondition(sheetId, conditionId));
  const dispatch = useDispatch();

  let hslColor = null;
  if (hslObj) {
    let { h, s, l } = hslObj;
    hslColor = `hsl(${h}, ${s * 100}%, ${l * 100}%)`;
  }

  const color =
    (type === "color" ? conditionFillColor : hslColor) ||
    reduxFillColor ||
    defaultFillColor;

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    let { r, g, b } = newColor.rgb;
    let rgb = `rgb(${r},${g},${b})`;

    let updatedCondition = {
      id: conditionId,
      fillColor: rgb,
      hslObj: newColor.hsl,
    };
    // update color reference
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition,
      }),
    );
    // if heatmap toggles, update all rooms
    if (type === "heatmapColor") {
      const heatMapObj = heatMapObjfromJson(catObj, newColor.hsl);
      dispatch(
        actionSheetHeatmapColorUpdated({
          conditionId,
          sheetId,
          heatMapObj,
          newColor,
        }),
      );
    }
  };

  return (
    <SettingColorSelector
      fillColor={color}
      handleNewColor={handleNewColor}
      label="Fill Color"
      labelIcon={<FormatColorFillIcon />}
      {...rest}
    />
  );
};

const LineColorSelector = ({ sheetId, conditionId, ...rest }) => {
  const { lineColor: conditionLineColor } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const { lineColor: reduxLineColor } = useSelector(selectorSheet(sheetId));
  const { lineColor: defaultLineColor } = defaultSheetFile(sheetId);
  const dispatch = useDispatch();

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          lineColor: newColor.hex,
        },
      }),
    );
  };

  const color = conditionLineColor || reduxLineColor || defaultLineColor;

  return (
    <SettingColorSelector
      fillColor={color}
      handleNewColor={handleNewColor}
      label="Border"
      labelIcon={<BrushIcon />}
      {...rest}
    />
  );
};

const TextColorSelector = ({ sheetId, conditionId, ...rest }) => {
  const { textColor: reduxTextColor } = useSelector(selectorSheet(sheetId));
  const { textColor: conditionTextColor } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const { textColor: defaultTextColor } = defaultSheetFile(sheetId);
  const dispatch = useDispatch();

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          textColor: newColor.hex,
        },
      }),
    );
  };

  const color = conditionTextColor || reduxTextColor || defaultTextColor;

  return (
    <SettingColorSelector
      fillColor={color}
      labelIcon={<FormatColorTextIcon />}
      handleNewColor={handleNewColor}
      label="Text Color"
      {...rest}
    />
  );
};

const IconColorSelector = ({ sheetId, conditionId, ...rest }) => {
  const { iconColor: defaultIconColor } = useSelector(selectorSheet(sheetId));
  const { iconColor: conditionIconColor } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          iconColor: newColor.hex,
        },
      }),
    );
  };

  const color = conditionIconColor || defaultIconColor;

  return (
    <SettingColorSelector
      fillColor={color}
      labelIcon={<ColorizeIcon />}
      handleNewColor={handleNewColor}
      label="Icon Color"
      {...rest}
    />
  );
};

const PointColorSelector = ({ sheetId, conditionId, ...rest }) => {
  const { pointColor: defaultPointColor } = defaultSheetFile(sheetId);
  const { pointColor: reduxPointColor } = useSelector(selectorSheet(sheetId));
  const {
    pointColor: conditionPointColor,
    type,
    catObj,
    hslObj,
  } = useSelector(selectorSheetCondition(sheetId, conditionId));
  const dispatch = useDispatch();

  let hslColor = null;
  if (hslObj) {
    let { h, s, l } = hslObj;
    hslColor = `hsl(${h}, ${s * 100}%, ${l * 100}%)`;
  }

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          pointColor: newColor.hex,
          hslObj: newColor.hsl,
        },
      }),
    );
    if (type === "heatmapPoint") {
      const heatMapObj = heatMapObjfromJson(catObj, newColor.hsl);
      dispatch(
        actionSheetHeatmapColorUpdated({
          conditionId,
          sheetId,
          heatMapObj,
          newColor,
        }),
      );
    }
  };

  const color =
    (type === "point" ? conditionPointColor : hslColor) ||
    reduxPointColor ||
    defaultPointColor;

  return (
    <SettingColorSelector
      fillColor={color}
      labelIcon={<ColorizeIcon />}
      handleNewColor={handleNewColor}
      label="Point Color"
      {...rest}
    />
  );
};

const TextInput = ({ sheetId, conditionId, ...rest }) => {
  const { text: conditionText } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();

  const handleTextChange = (event) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          text: event.target.value,
        },
      }),
    );
  };

  const text = conditionText || "";

  return (
    <Setting
      label="Text"
      labelIcon={<TextFieldsIcon />}
      {...rest}
      controller={
        <TextField
          id={`cond${conditionId}-text`}
          size="small"
          variant="standard"
          placeholder="Enter text"
          value={text}
          onChange={handleTextChange}
        />
      }
    />
  );
};

const TypeSelector = (props) => {
  const handleChange = (event) => {
    props.handleTypeSet(event.target.value);
  };

  return (
    <Setting
      active={props.active}
      label="Field Value Type"
      labelIcon={<CategoryIcon />}
      controller={
        <FormControl
          variant="standard"
          component="fieldset"
          disabled={!props.active}
        >
          <RadioGroup
            row
            aria-label="field value type"
            name="field-value"
            value={props.type}
            onChange={handleChange}
          >
            <FormControlLabel
              value="category"
              control={
                <Radio
                  slotProps={{ input: { sx: radioChecked_ } }}
                  color="default"
                />
              }
              label="Category"
            />
            <FormControlLabel
              value="number"
              control={
                <Radio
                  slotProps={{ input: { sx: radioChecked_ } }}
                  color="default"
                />
              }
              label="Number"
            />
            <FormControlLabel
              value="date"
              control={
                <Radio
                  slotProps={{ input: { sx: radioChecked_ } }}
                  color="default"
                />
              }
              label="Date"
            />
          </RadioGroup>
        </FormControl>
      }
    />
  );
};

const ConditionHeader = (props) => {
  return (
    <Box sx={conditionHeader_}>
      <Typography>{props.text}</Typography>
      <Tooltip title="Remove Condition">
        <IconButton
          aria-label="remove condition"
          onClick={props.handleIconClick}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ColorCondition = ({
  sheetId,
  active,
  conditionId,
  column,
  type: dataType,
}) => {
  const { type: mapType } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const { conditions } = useSelector(selectorSheet(sheetId));
  const dispatch = useDispatch();

  const handleConditionRemoval = () => {
    dispatch(
      actionSheetConditionRemoved({
        id: sheetId,
        conditionId,
      }),
    );
    if (mapType.indexOf("heatmap") === 0) {
      conditions
        .filter((condition) => condition.heatMapConditionRef === conditionId)
        .forEach((condition) => {
          dispatch(
            actionSheetConditionRemoved({
              id: sheetId,
              conditionId: condition.id,
            }),
          );
        });
    }
  };

  const isHeatmap = mapType.indexOf("heatmap") === 0;

  return (
    <Paper sx={condition_}>
      <ConditionHeader
        handleIconClick={handleConditionRemoval}
        text="Color Conditional"
      />
      <RRNSelector
        dataType={dataType}
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
        column={column}
        showHeatmap={true}
      />
      <FillColorSelector
        sheetId={sheetId}
        active={active}
        conditionId={conditionId}
      />
      <OpacitySlider
        sheetId={sheetId}
        active={active && !isHeatmap}
        conditionId={conditionId}
      />
      <LineColorSelector
        sheetId={sheetId}
        active={active && !isHeatmap}
        conditionId={conditionId}
      />
    </Paper>
  );
};

const TextCondition = (props) => {
  const { sheetId, active, conditionId, column, type: dataType } = props;

  const dispatch = useDispatch();

  const handleConditionRemoval = () => {
    dispatch(
      actionSheetConditionRemoved({
        id: sheetId,
        conditionId,
      }),
    );
  };

  return (
    <Paper sx={condition_}>
      <ConditionHeader
        handleIconClick={handleConditionRemoval}
        text="Text Conditional"
      />
      <RRNSelector
        dataType={dataType}
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
        column={column}
      />
      <TextColorSelector
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
      <TextSizeSlider
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
      <TextInput sheetId={sheetId} conditionId={conditionId} active={active} />
    </Paper>
  );
};

const IconCondition = (props) => {
  const { sheetId, active, conditionId, column, type: dataType } = props;

  const dispatch = useDispatch();

  const handleConditionRemoval = () => {
    dispatch(
      actionSheetConditionRemoved({
        id: sheetId,
        conditionId,
      }),
    );
  };

  return (
    <Paper sx={condition_}>
      <ConditionHeader
        handleIconClick={handleConditionRemoval}
        text="Icon Conditional"
      />
      <RRNSelector
        dataType={dataType}
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
        column={column}
      />
      <IconSelector
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
      <IconSizeSlider
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
      <IconColorSelector
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
    </Paper>
  );
};

const PointCondition = (props) => {
  const { sheetId, active, conditionId, column, type: dataType } = props;
  const { type: mapType } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const { conditions } = useSelector(selectorSheet(sheetId));

  const dispatch = useDispatch();

  const handleConditionRemoval = () => {
    dispatch(
      actionSheetConditionRemoved({
        id: sheetId,
        conditionId,
      }),
    );
    if (mapType.indexOf("heatmap") === 0) {
      conditions
        .filter((condition) => condition.heatMapConditionRef === conditionId)
        .forEach((condition) => {
          dispatch(
            actionSheetConditionRemoved({
              id: sheetId,
              conditionId: condition.id,
            }),
          );
        });
    }
  };

  const isHeatmap = mapType === "heatmapPoint";

  return (
    <Paper sx={condition_}>
      <ConditionHeader
        handleIconClick={handleConditionRemoval}
        text="Point Conditional"
      />
      <RRNSelector
        dataType={dataType}
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
        column={column}
        showHeatmap={true}
      />
      <PointIconSelector
        sheetId={sheetId}
        conditionId={conditionId}
        active={active && !isHeatmap}
      />
      <PointSizeSlider
        sheetId={sheetId}
        conditionId={conditionId}
        active={active && !isHeatmap}
      />
      <PointColorSelector
        sheetId={sheetId}
        conditionId={conditionId}
        active={active}
      />
    </Paper>
  );
};

const RRNSelector = ({ dataType, ...rest }) => {
  switch (dataType) {
    case "category":
      return <CategoryRRNSelect {...rest} />;
    case "number":
      return <NumberRRNSelect {...rest} />;
    case "date":
      return <DateRRNSelect {...rest} />;
    default:
      return null;
  }
};

const catObjFromJson = (json, rmrecnbrCol, valueCol) => {
  /*
  pair rmrecnbrs with label values, omitting header values
  [{ rmrecnbr: '1234567', label: 'example'}, ....]
  */
  const rmrecnbrPairs = json
    .map((row) => {
      return {
        rmrecnbr: row[rmrecnbrCol],
        label: row[valueCol],
      };
    })
    .filter((x, i) => i !== 0);
  /*
  create obj to group related label values
  property names are labels, property values are arrays of rmrecnbrs (as strings)
  {
    'example': ['1234567', '7654321'],
    'example2': ['']
  }
  */
  const catObj = {};
  rmrecnbrPairs.forEach((pair) => {
    if (!pair.rmrecnbr) return;
    if (catObj[pair.label]) {
      catObj[pair.label] = [...catObj[pair.label], pair.rmrecnbr];
    } else {
      catObj[pair.label] = [pair.rmrecnbr];
    }
  });
  return catObj;
};

const pointCatObjFromJson = (json, valueCol, sheetId) => {
  function isPointValue(row, lngCol, latCol, typeCol) {
    if (typeCol && row[typeCol] === "Point") return true;
    if (row[lngCol] && row[latCol]) return true;
    return false;
  }

  const lngCol = json[0].findIndex((x) =>
    ["lng", "longitude", "long"].includes(String(x).toLowerCase()),
  );
  const latCol = json[0].findIndex((x) =>
    ["lat", "latitude", "lat"].includes(String(x).toLowerCase()),
  );
  const typeCol = json[0].indexOf("type");

  /*
  pair rmrecnbrs with label values, omitting header values
  [{ pointId: 'point-1-1', label: 'example'}, ....]
  */
  // TODO filter blank labels
  const pointIdPairs = json
    .map((row, i) => {
      if (isPointValue(row, lngCol, latCol, typeCol)) {
        return {
          pointId: `point-${sheetId}-${i}`,
          label: row[valueCol],
        };
      }
    })
    .filter((x, i) => i !== 0 && x);
  /*
  create obj to group related label values
  property names are labels, property values are arrays of rmrecnbrs (as strings)
  {
    'example': ['point-0-1', 'point-0-2'],
    'example2': ['point-0-3']
  }
  */
  const catObj = {};
  pointIdPairs.forEach((pair) => {
    if (!pair.pointId) return;
    if (catObj[pair.label]) {
      catObj[pair.label] = [...catObj[pair.label], pair.pointId];
    } else {
      catObj[pair.label] = [pair.pointId];
    }
  });
  return catObj;
};

const NumberRRNSelect = ({
  sheetId,
  column,
  conditionId,
  active,
  showHeatmap = false,
}) => {
  const {
    json,
    rmrecnbr: rmrecnbrCol,
    conditions,
  } = useSelector(selectorSheet(sheetId));
  const { type, value } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const dispatch = useDispatch();
  const [minError, setMinError] = useState(false);
  const [maxError, setMaxError] = useState(false);

  const min = value?.min || "";
  const max = value?.max || "";

  const firstHeatMap =
    conditions.filter((condition) =>
      ["heatmapColor", "heatmapPoint", "color", "point"].includes(
        condition.type,
      ),
    ).length > 0
      ? conditions.find((condition) =>
          ["heatmapColor", "heatmapPoint", "color", "point"].includes(
            condition.type,
          ),
        ).id
      : null;

  const catObj =
    type === "point" || type === "heatmapPoint"
      ? pointCatObjFromJson(json, column, sheetId)
      : catObjFromJson(json, rmrecnbrCol, column);
  const keysArray = Object.keys(catObj);
  const numArray = keysArray
    .map((num) => parseNumber(num))
    .filter((num) => typeof num === "number");
  const mathMin = Math.min(...numArray);
  const mathMax = Math.max(...numArray);
  const defaultMin = numArray.length > 0 ? mathMin.toString() : "";
  const defaultMax = numArray.length > 0 ? mathMax.toString() : "";
  const step = calculateStepValue(keysArray);
  const hasDollarAdornment =
    keysArray.length > 0 && keysArray.every((num) => num.startsWith("$"));

  useEffect(() => {
    if (defaultMin === "" && defaultMax === "") {
      setMinError(true);
      setMaxError(true);
    }
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: defaultMin,
            max: defaultMax,
          },
        },
      }),
    );
  }, []); // run only on initial render

  /*
  We have to re-pass the unchanged min or max values for the complementary handlers
  (min for handleMax, max for handleMin) 
  because we are not using the "..."" spread operator for the value object in the reducer
  */
  const handleMax = (e) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: min,
            max: e.target.value,
          },
        },
      }),
    );
    if (e.target.value === "") {
      setMaxError(true);
    } else {
      setMaxError(false);
    }
  };

  const handleMin = (e) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: e.target.value,
            max: max,
          },
        },
      }),
    );
    if (e.target.value === "") {
      setMinError(true);
    } else {
      setMinError(false);
    }
  };

  // use catObj to combine arrays of rmrecnbrs whose values fall in the selected range
  const calcRange = (min, max) => {
    const rmrecnbrArray = [];
    const parsedMin = parseNumber(min);
    const parsedMax = parseNumber(max);
    for (const num in catObj) {
      const parsedNum = parseNumber(num);
      if (parsedNum <= parsedMax && parsedNum >= parsedMin) {
        rmrecnbrArray.push(...catObj[num]);
      }
    }
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          rmrecnbr: rmrecnbrArray,
        },
      }),
    );
  };

  useEffect(() => {
    calcRange(min, max);
  }, [min, max]);

  const activeVal = active && type.indexOf("heatmap") !== 0;

  return (
    <>
      {showHeatmap && conditionId === firstHeatMap ? (
        <HeatMapSwitch
          sheetId={sheetId}
          conditionId={conditionId}
          active={active}
          catObj={catObj}
        />
      ) : (
        ""
      )}
      <Setting
        label="Lower Boundary"
        active={activeVal}
        labelIcon={<ArrowDownwardIcon />}
        error={minError}
        controller={
          <TextField
            disabled={!activeVal}
            size="small"
            fullWidth
            type="number"
            id={`cond-${conditionId}-min`}
            variant="standard"
            value={min}
            error={minError}
            helperText={minError && "Must not be blank"}
            onChange={handleMin}
            slotProps={{
              htmlInput: {
                "aria-label": "Lower Boundary",
                min: defaultMin,
                max: defaultMax,
                step: step,
              },
              input: {
                startAdornment: hasDollarAdornment ? (
                  <InputAdornment position="start">$</InputAdornment>
                ) : null,
              },
            }}
          />
        }
      />
      <Setting
        label="Upper Boundary"
        active={activeVal}
        labelIcon={<ArrowUpwardIcon />}
        error={maxError}
        controller={
          <TextField
            disabled={!activeVal}
            size="small"
            fullWidth
            type="number"
            id={`cond-${conditionId}-max`}
            variant="standard"
            value={max}
            error={maxError}
            helperText={maxError && "Must not be blank"}
            onChange={handleMax}
            slotProps={{
              htmlInput: {
                "aria-label": "Upper Boundary",
                min: defaultMin,
                max: defaultMax,
                step: step,
              },
              input: {
                startAdornment: hasDollarAdornment ? (
                  <InputAdornment position="start">$</InputAdornment>
                ) : null,
              },
            }}
          />
        }
      />
    </>
  );
};

const calculateStepValue = (keysArray) => {
  const decimalPlaceArray = keysArray
    .map((num) => String(parseNumber(num))) // convert to num to get rid of formatting, convert back to string to split
    .map((num) => num.split(".")[1]?.length || 0); // get decimal place length for each number
  const maxDecimals = Math.max(...decimalPlaceArray);

  if (maxDecimals > 0) {
    return `0.${"0".repeat(maxDecimals - 1)}1`;
  } else {
    return "1";
  }
};

const DateRRNSelect = ({ sheetId, column, conditionId, active }) => {
  const { json, rmrecnbr: rmrecnbrCol } = useSelector(selectorSheet(sheetId));
  const { type, value } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );
  const [minError, setMinError] = useState(false);
  const [maxError, setMaxError] = useState(false);
  const dispatch = useDispatch();

  // Must be 'YYYY-MM-DD' format for native datepicker
  const DATE_FORMAT = "YYYY-MM-DD";
  const min = value?.min || "";
  const max = value?.max || "";

  /*
    * catObj format
    {
      * Sheetjs date import format as key
      'Mon Jan 1 2023 00:00:00 GMT-0500 (Eastern Standard Time)':
        * Array of rmrecnbrs as value
        [111111, 111112],
      'Tue Jan 1 2023 00:00:00 GMT-0500 (Eastern Standard Time)': [111113],
      ...
    }
  */

  const catObj =
    type === "point"
      ? pointCatObjFromJson(json, column, sheetId)
      : catObjFromJson(json, rmrecnbrCol, column);
  // convert date keys to dayjs date
  const dateArray = Object.keys(catObj)
    .map((date) => dayjs(date))
    .filter((date) => date.isValid());
  // sort in chronological order
  const sortedDateArray = [...dateArray].sort((a, b) => {
    if (a.isBefore(b, "day")) return -1;
    if (b.isBefore(a, "day")) return 1;
    return 0;
  });

  // format min and max dayjs dates as 'YYYY-MM-DD' for inputs
  const defaultMin =
    sortedDateArray.length > 0 ? sortedDateArray[0].format(DATE_FORMAT) : "";
  const defaultMax =
    sortedDateArray.length > 0
      ? sortedDateArray[sortedDateArray.length - 1].format(DATE_FORMAT)
      : "";

  useEffect(() => {
    if (!defaultMin && !defaultMax) {
      setMinError(true);
      setMaxError(true);
    }
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: defaultMin,
            max: defaultMax,
          },
        },
      }),
    );
  }, []); // run only on initial render

  /*
  We have to re-pass the unchanged min and max values for the handlers
  (min for handleMax, max for handleMin) 
  because we are not using the "..."" spread operator for the value object in the reducer
  */
  const handleMax = (e) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: min,
            max: e.target.value,
          },
        },
      }),
    );
    setMaxError(e.target.validity.badInput);
  };

  const handleMin = (e) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          value: {
            min: e.target.value,
            max: max,
          },
        },
      }),
    );
    setMinError(e.target.validity.badInput);
  };

  const calcRange = (min, max) => {
    const rmrecnbrArray = [];
    // create dayjs dates from input min max for comparisons
    const dayMin = dayjs(min, DATE_FORMAT);
    const dayMax = dayjs(max, DATE_FORMAT);
    // use catObj to combine arrays of rmrecnbrs whose values fall in the selected range
    for (const date in catObj) {
      const d = dayjs(date);
      if (min && max) {
        if (d.isSameOrBefore(dayMax, "day") && d.isSameOrAfter(dayMin, "day")) {
          rmrecnbrArray.push(...catObj[date]);
        }
      } else {
        if (d.isSameOrBefore(dayMax, "day")) {
          rmrecnbrArray.push(...catObj[date]);
        }
        if (d.isSameOrAfter(dayMin, "day")) {
          rmrecnbrArray.push(...catObj[date]);
        }
      }
    }

    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          rmrecnbr: rmrecnbrArray,
        },
      }),
    );
  };

  useEffect(() => {
    calcRange(min, max);
  }, [min, max]);

  return (
    <>
      <Setting
        label="Start"
        active={active}
        error={minError}
        labelIcon={<ArrowForwardIcon />}
        controller={
          <TextField
            disabled={!active}
            size="small"
            fullWidth
            type="date"
            sx={date_}
            id={`cond-${conditionId}-datemin`}
            variant="standard"
            value={min}
            error={minError}
            helperText={minError && "Invalid date"}
            onChange={handleMin}
            slotProps={{
              htmlInput: {
                "data-empty": !min,
                min: defaultMin,
                max: defaultMax,
              },
            }}
          />
        }
      />
      <Setting
        label="End"
        active={active}
        error={maxError}
        labelIcon={<ArrowBackIcon />}
        controller={
          <TextField
            disabled={!active}
            size="small"
            fullWidth
            type="date"
            sx={date_}
            id={`cond-${conditionId}-datemax`}
            variant="standard"
            value={max}
            error={maxError}
            helperText={maxError && "Invalid date"}
            onChange={handleMax}
            slotProps={{
              htmlInput: {
                "data-empty": !max,
                min: defaultMin,
                max: defaultMax,
              },
            }}
          />
        }
      />
    </>
  );
};

const CategoryRRNSelect = ({ sheetId, column, conditionId, ...rest }) => {
  const { json, rmrecnbr: rmrecnbrCol } = useSelector(selectorSheet(sheetId));
  const { rmrecnbr: rmrecnbrArray } = useSelector(
    selectorSheetCondition(sheetId, conditionId),
  );

  const dispatch = useDispatch();
  const { type } = useSelector(selectorSheetCondition(sheetId, conditionId));
  const catObj =
    type === "point"
      ? pointCatObjFromJson(json, column, sheetId)
      : catObjFromJson(json, rmrecnbrCol, column);

  const value =
    Object.keys(catObj).find((key) => _.isEqual(catObj[key], rmrecnbrArray)) ||
    "";
  const optionsArray = Object.keys(catObj).map((key) => {
    return {
      value: key,
      label: key,
    };
  });

  const handleChange = (key) => {
    dispatch(
      actionSheetConditionUpdated({
        id: sheetId,
        updatedCondition: {
          id: conditionId,
          rmrecnbr: catObj[key],
        },
      }),
    );
  };

  return (
    <SettingDropSelector
      labelIcon={<VpnKeyIcon />}
      label="Field Value"
      formControlClass={formControl_}
      handleSelection={handleChange}
      value={value}
      optionsArray={optionsArray}
      defaultOption={{
        value: "",
        label: "Select",
        disabled: true,
      }}
      {...rest}
    />
  );
};

const conditionIsBlank = (conditions, mapType) => {
  if (Array.isArray(mapType)) {
    conditions = conditions.filter((condition) =>
      mapType.includes(condition.type),
    );
  } else {
    conditions = conditions.filter((condition) => condition.type === mapType);
  }
  if (conditions.length === 0) return false;
  const condition = conditions[conditions.length - 1];
  // only one heatmap condition allowed
  if (condition.isHeatMapCondition) return true;
  return (
    condition.rmrecnbr.length === 0 &&
    !condition.fillColor &&
    !condition.fillOpacity &&
    !condition.lineColor
  );
};

const Conditional = (props) => {
  const { sheetId, active, mapType, attrKey, dataKey, conditionArray } = props;
  const {
    conditions,
    [attrKey]: conditionalAttr,
    [dataKey]: conditionalDatatype,
  } = useSelector(selectorSheet(sheetId));
  const dispatch = useDispatch();

  const handleColSelect = (col) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [attrKey]: col,
      }),
    );
    if (conditions.length > 0) {
      clearConditions(conditionArray);
    }
  };

  const handleTypeSet = (type) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [dataKey]: type,
      }),
    );
    if (conditions.length > 0) {
      clearConditions(conditionArray);
    }
  };

  const clearConditions = (typesArray) => {
    conditions
      .filter((condition) => typesArray.includes(condition.type))
      .forEach((condition) => {
        dispatch(
          actionSheetConditionRemoved({
            id: sheetId,
            conditionId: condition.id,
          }),
        );
      });
  };

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography>Step 1. Choose attribute</Typography>
      <TextSelector
        sheetId={sheetId}
        active={active}
        handleColSelect={handleColSelect}
        column={conditionalAttr}
        labelIcon={<VpnKeyIcon />}
      />
      <TypeSelector
        handleTypeSet={handleTypeSet}
        type={conditionalDatatype}
        active={active}
      />
      <Conditions
        sheetId={sheetId}
        active={active}
        conditionArray={conditionArray}
        attrKey={attrKey}
        mapType={mapType}
        dataKey={dataKey}
      />
    </Box>
  );
};

const Conditions = ({
  sheetId,
  active,
  conditionArray,
  attrKey,
  dataKey,
  mapType,
}) => {
  const {
    conditions,
    [attrKey]: conditionalAttr,
    [dataKey]: conditionalDatatype,
  } = useSelector(selectorSheet(sheetId));

  const dispatch = useDispatch();
  const canAddCondition = conditionIsBlank(conditions, conditionArray);
  const validAttr = Number.isInteger(conditionalAttr);

  const handleButtonClick = () => {
    const nextId =
      conditions.length > 0
        ? Math.max(...conditions.map((condition) => parseInt(condition.id))) + 1
        : 0;
    const newCondition = {
      id: nextId,
      rmrecnbr: [],
      type: mapType,
    };
    dispatch(
      actionSheetConditionAdded({
        id: sheetId,
        newCondition,
      }),
    );
  };

  if (!conditionalDatatype || !validAttr) {
    return "";
  }

  return (
    <>
      <Typography>Step 2. Add condition</Typography>
      {conditions
        .filter(
          (condition) =>
            conditionArray.includes(condition.type) &&
            !condition.isHeatMapCondition,
        )
        .map((condition) => {
          switch (mapType) {
            case "text":
              return (
                <TextCondition
                  type={conditionalDatatype}
                  column={conditionalAttr}
                  key={condition.id}
                  conditionId={condition.id}
                  sheetId={sheetId}
                  active={active}
                />
              );
            case "icon":
              return (
                <IconCondition
                  type={conditionalDatatype}
                  column={conditionalAttr}
                  key={condition.id}
                  conditionId={condition.id}
                  sheetId={sheetId}
                  active={active}
                />
              );
            case "color":
              return (
                <ColorCondition
                  type={conditionalDatatype}
                  column={conditionalAttr}
                  key={condition.id}
                  conditionId={condition.id}
                  sheetId={sheetId}
                  active={active}
                />
              );
            case "point":
              return (
                <PointCondition
                  type={conditionalDatatype}
                  column={conditionalAttr}
                  key={condition.id}
                  conditionId={condition.id}
                  sheetId={sheetId}
                  active={active}
                />
              );
            default:
              return "";
          }
        })}
      <Button
        disabled={canAddCondition}
        sx={button_}
        startIcon={<AddIcon />}
        onClick={handleButtonClick}
        variant="contained"
      >
        Condition
      </Button>
    </>
  );
};

export const ColorConditional = (props) => {
  const { sheetId, active } = props;

  return (
    <Conditional
      sheetId={sheetId}
      active={active}
      mapType="color"
      attrKey="colorConditionalAttr"
      dataKey="colorConditionalDatatype"
      conditionArray={["color", "heatmapColor"]}
    />
  );
};

export const TextConditional = (props) => {
  const { sheetId, active } = props;

  return (
    <Conditional
      sheetId={sheetId}
      active={active}
      mapType="text"
      attrKey="textConditionalAttr"
      dataKey="textConditionalDatatype"
      conditionArray={["text"]}
    />
  );
};

export const IconConditional = (props) => {
  const { sheetId, active } = props;
  return (
    <Conditional
      sheetId={sheetId}
      active={active}
      mapType="icon"
      attrKey="iconConditionalAttr"
      dataKey="iconConditionalDatatype"
      conditionArray={["icon"]}
    />
  );
};

export const PointConditional = (props) => {
  const { sheetId, active } = props;
  return (
    <Conditional
      sheetId={sheetId}
      active={active}
      mapType="point"
      attrKey="pointConditionalAttr"
      dataKey="pointConditionalDatatype"
      conditionArray={["point", "heatmapPoint"]}
    />
  );
};
